import React from 'react';
import { Grid, IconButton, Typography, OutlinedInput, InputAdornment, AppBar, Toolbar, Tooltip } from '@mui/material';
import { Close, Send } from '@material-ui/icons';
import { Download } from '@mui/icons-material';

// Ícones simples como fallback (você pode substituir por ícones reais)
const fileIcons = {
  json: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjQ4IiByeD0iNCIgZmlsbD0iIzI5NzNGRiIvPgo8dGV4dCB4PSIyNCIgeT0iMjgiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkpTT048L3RleHQ+Cjwvc3ZnPgo=',
  pdf: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjQ4IiByeD0iNCIgZmlsbD0iI0ZGNTU1NSIvPgo8dGV4dCB4PSIyNCIgeT0iMjgiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiPlBERjwvdGV4dD4KPC9zdmc+Cg==',
};

const FilePreview = ({ selectedFile, setSelectedFile, handleSendMessage, newMessage, setNewMessage, isChat }) => {
  const getFileIcon = (file) => {
    const extension = typeof file === 'string' ? file.split('.').pop().toLowerCase() : file.name.split('.').pop().toLowerCase();
    return fileIcons[extension] || fileIcons.pdf;
  };

  const isImage = (file) => {
    if (typeof file === 'string') {
      return file.startsWith('data:image') || file.match(/\.(jpeg|jpg|gif|png|webp)$/i);
    }
    return file.type?.startsWith('image/');
  };

  const isPdf = (file) => {
    if (typeof file === 'string') {
      return file.match(/\.pdf$/i) || file.includes('pdf');
    }
    return file.type === 'application/pdf' || file.name?.match(/\.pdf$/i);
  };

  const downloadFile = () => {
    let fileURL;
    let fileName = selectedFile.name || 'download';

    // Se selectedFile é uma string (URL), extrai o nome do arquivo da URL
    if (typeof selectedFile === 'string') {
      const urlParts = selectedFile.split('/');
      fileName = urlParts[urlParts.length - 1] || 'download';
      
      // Para URLs, faz o download diretamente
      const link = document.createElement('a');
      link.href = selectedFile;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Se selectedFile é um Blob ou File, usa diretamente o URL criado
      fileURL = URL.createObjectURL(selectedFile);
      triggerDownload(fileURL, fileName);
    }
  };

  const triggerDownload = (fileURL, fileName) => {
    const link = document.createElement('a');
    link.href = fileURL;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Libera o URL temporário do objeto para evitar consumo de memória
    URL.revokeObjectURL(fileURL);
  };

  return (
    <Grid
      container
      sx={{
        padding: '1rem',
        backgroundColor: 'var(--surface-elevated)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        /*
         * `nowrap` -- e e ESTE o conserto.
         *
         * `Grid container` do MUI traz `flex-wrap: wrap`. Com
         * `flex-direction: column` e altura FIXA (os 80% abaixo), o que nao
         * cabe na coluna nao e cortado nem rolado: ele quebra numa SEGUNDA
         * COLUNA, que o navegador desenha ao lado -- fora da caixa.
         *
         * Era isso que a Gisele via (ponto 2, 20/10/2026): "a imagem ta
         * ficando fora do modal que abre". Medido numa reproducao do mesmo
         * CSS, com uma foto de celular em retrato: a imagem nascia 556px a
         * direita da borda do modal, e o modal ficava VAZIO. Com `nowrap`,
         * dentro.
         *
         * O `overflow: hidden` do bloco da imagem nao salvava: ele age sobre
         * o que transborda do proprio bloco, e aqui quem se mudou de lugar
         * foi o bloco inteiro.
         */
        flexWrap: 'nowrap',
        alignItems: 'center',
        marginTop: '1rem',
        marginLeft: '1rem',
        width: '66%',
        // 66% + a margem de 1rem passa da largura da tela em janela estreita,
        // e o widget vive embutido em espaco pequeno.
        maxWidth: 'calc(100vw - 2rem)',
        boxSizing: 'border-box',
        height: isChat ? '80%' : '80%',
        left: isChat ? '' : '15%',
        position: 'fixed',
        bottom: '10%',
        zIndex: 10,
        justifyContent: 'space-between',
      }}
    >
      {/* Barra Superior com Botões */}
      <AppBar position="static" color="transparent" elevation={0} sx={{ width: '100%', borderRadius: '8px 8px 0 0' }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Grid container flexWrap={'nowrap'} flexDirection={'row'} sx={{ bgcolor: 'transparent' }}>
            <Grid container item sx={{ bgcolor: "transparent" }}>
              {/* Botão para remover o arquivo */}
              <Tooltip title={"Fechar"}>
                <IconButton onClick={() => setSelectedFile(null)} size="small">
                  <Close />
                </IconButton>
              </Tooltip>
              <Typography variant="h6">Pré-visualização</Typography>
            </Grid>
            {!handleSendMessage && (
              <Grid item sx={{ bgcolor: "transparent" }}>
                {/* Botão para download */}
                <Tooltip title={"Download"}>
                  <IconButton onClick={downloadFile} size="small">
                    <Download />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </Toolbar>
      </AppBar>
      
      <Grid item sx={{ bgcolor: 'transparent', flex: '1 1 auto', minHeight: 0, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {isImage(selectedFile) ? (
          <img
            src={typeof selectedFile === 'string' ? selectedFile : URL.createObjectURL(selectedFile)}
            alt="Pré-visualização"
            // Cabe no espaço disponível (não estoura o container nem empurra a caixa
            // de texto para fora da tela); antes tinha tamanho fixo em rem, o que
            // obrigava o cliente a diminuir o zoom da página.
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '8px' }}
          />
        ) : isPdf(selectedFile) ? (
          <iframe
            src={typeof selectedFile === 'string' ? selectedFile : URL.createObjectURL(selectedFile)}
            style={{ 
              width: '100%', 
              height: isChat ? '35rem' : '70rem', 
              border: 'none',
              borderRadius: '8px'
            }}
            title="Pré-visualização do PDF"
          />
        ) : (
          <img
            src={getFileIcon(selectedFile)}
            alt="Ícone do Arquivo"
            style={{ width: '50px', height: '50px' }}
          />
        )}
      </Grid>
      
      {/* Visualização do arquivo */}
      <Grid container flexDirection={'column'} alignContent={'center'} alignItems={'center'} justifyContent={'center'} item
        sx={{ bgcolor: 'transparent' }}>

        {/* Caixa de texto para mensagem abaixo da imagem */}
        {handleSendMessage && (
          <Grid item sx={{ width: '100%', marginTop: '1rem', padding: '0 1rem', bgcolor: 'transparent' }}>
            <OutlinedInput
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              fullWidth
              placeholder="Digite uma mensagem para enviar com o arquivo"
              endAdornment={
                <InputAdornment position="end">
                  <IconButton onClick={handleSendMessage} size="small" color="primary">
                    <Send />
                  </IconButton>
                </InputAdornment>
              }
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                '& .MuiInputBase-input': {
                  padding: '8px 12px',
                },
              }}
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default FilePreview;
